// safety/safety-walks/pages/SafetyWalkDetailPage.tsx
// 4-tab walk detail: Overview | Observations | Workflow | Audit History
// (Figma v31 — PDF Reports is NOT a tab; header PDF actions remain).
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Loader2, Plus, Share2, Mail,  Download, CheckCircle2, Circle, Loader, Upload, ImageIcon, X, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useDropzone } from 'react-dropzone';
import { useAbility } from '@casl/react';
import { AbilityContext } from '../../../../context/casl/AbilityContext';
import { ACTIONS, RESOURCES } from '../../../../constants/permissions.constants';
import { Combobox } from '../../../../components/ui/combobox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/ui/select';
import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogCancel,
} from '../../../../components/ui/alert-dialog';
import { getWalk, listWalkObservations, addObservationToWalk, completeWalk, uploadPhoto, downloadWalkPdf, updateWalk, deleteWalk } from '../../../../utils/safety-walks-api';
import api from '../../../../utils/api';
import { projectsApi } from '../../../../utils/lists-api';
import { StatusPill, RiskBadge, DueIndicator, InfoCell, fmtDate, fmtDateTime, closeOutSubmission } from '../components/shared';
import { EditSafetyWalkDrawer } from '../components/EditSafetyWalkDrawer';
import type { WalkFormValue, SelectionUser, ProjectRef } from '../components/WalkFormFields';
import { RISK_RATINGS, OBSERVATION_CATEGORIES } from '../../../../types/safety-walk.types';
import type { SafetyWalk, SafetyObservation, WalkWorkflow, RiskRating } from '../../../../types/safety-walk.types';

type TabKey = 'overview' | 'observations' | 'workflow' | 'audit';
const TABS: { key: TabKey; label: string }[] = [
    { key: 'overview', label: 'Overview' },
    { key: 'observations', label: 'Observations' },
    { key: 'workflow', label: 'Workflow' },
    { key: 'audit', label: 'Audit History' },
];

export const SafetyWalkDetailPage: React.FC = () => {
    const { walkId } = useParams<{ walkId: string }>();
    const id = Number(walkId);
    const navigate = useNavigate();
    const ability = useAbility(AbilityContext);
    const [searchParams] = useSearchParams();
    const initialTab = searchParams.get('tab') as TabKey | null;

    // Safety Officers (verify grant) get the close-out review entry points
    const canVerifyObs = ability.can(ACTIONS.VERIFY, RESOURCES.SAFETY_OBSERVATION);
    // Overview editing (PATCH /api/safety/walks/:id) — same grant as Mark Complete
    const canUpdate = ability.can(ACTIONS.UPDATE, RESOURCES.SAFETY_WALK);
    // Draft-only deletion (DELETE /api/safety/walks/:id)
    const canDelete = ability.can(ACTIONS.DELETE, RESOURCES.SAFETY_WALK);

    const [walk, setWalk] = useState<SafetyWalk | null>(null);
    const [workflow, setWorkflow] = useState<WalkWorkflow | null>(null);
    const [observations, setObservations] = useState<SafetyObservation[]>([]);
    const [obsTotal, setObsTotal] = useState(0);
    const [tab, setTab] = useState<TabKey>(TABS.some((t) => t.key === initialTab) ? (initialTab as TabKey) : 'overview');
    const [loading, setLoading] = useState(true);
    const [completing, setCompleting] = useState(false);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [completeOpen, setCompleteOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [projects, setProjects] = useState<ProjectRef[]>([]);

    // New-observation inline form state
    const [showNewObs, setShowNewObs] = useState(false);
    const [obsTitle, setObsTitle] = useState('');
    const [obsCategory, setObsCategory] = useState('');
    const [obsDescription, setObsDescription] = useState('');
    const [obsRisk, setObsRisk] = useState<RiskRating>('Low');
    const [obsPersonId, setObsPersonId] = useState('');
    const [obsCompany, setObsCompany] = useState('');
    const [obsDue, setObsDue] = useState('');
    const [obsBeforePhotos, setObsBeforePhotos] = useState<File[]>([]);
    const [users, setUsers] = useState<SelectionUser[]>([]);
    const [savingObs, setSavingObs] = useState(false);

    // Load system users for the Person Responsible combobox
    useEffect(() => {
        projectsApi
            .list({ status: 'active' })
            .then((rows) => setProjects((rows || []).map((p) => ({ id: p.id, projectName: p.projectName, siteAddress: p.siteAddress }))))
            .catch(() => {});
        api.get('/api/users/selection')
            .then((res) => {
                if (res.success && Array.isArray(res.users)) setUsers(res.users);
            })
            .catch(() => {});
    }, []);

    // Before-photo picker — multiple files (FileAttachments-style)
    const onBeforePhotoDrop = useCallback((acceptedFiles: File[]) => {
        for (const file of acceptedFiles) {
            if (!['image/png', 'image/jpeg'].includes(file.type)) {
                return toast.error(`"${file.name}" — photo must be PNG or JPG`);
            }
            if (file.size > 10 * 1024 * 1024) {
                return toast.error(`"${file.name}" — photo exceeds maximum limit of 10MB`);
            }
        }
        setObsBeforePhotos((prev) => [...prev, ...acceptedFiles]);
    }, []);

    const removeBeforePhoto = (index: number) => {
        setObsBeforePhotos((prev) => prev.filter((_, i) => i !== index));
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop: onBeforePhotoDrop,
        accept: { 'image/png': ['.png'], 'image/jpeg': ['.jpg', '.jpeg'] },
        maxSize: 10 * 1024 * 1024,
        multiple: true,
    });

    const fetchDetail = useCallback(async () => {
        if (!id) return;
        setLoading(true);
        try {
            const [walkRes, obsRes] = await Promise.all([
                getWalk(id),
                listWalkObservations(id, { sortBy: 'createdAt', sortDir: 'desc', limit: 200 }),
            ]);
            setWalk(walkRes.walk || null);
            setWorkflow(walkRes.workflow || null);
            setObservations(obsRes.observations || []);
            setObsTotal(obsRes.total || 0);
        } catch {
            // api util toasts
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchDetail();
    }, [fetchDetail]);

    const refreshObs = useCallback(async () => {
        try {
            const obsRes = await listWalkObservations(id, { sortBy: 'createdAt', sortDir: 'desc', limit: 200 });
            setObservations(obsRes.observations || []);
            setObsTotal(obsRes.total || 0);
            const walkRes = await getWalk(id);
            setWalk(walkRes.walk || null);
            setWorkflow(walkRes.workflow || null);
        } catch { /* toasts */ }
    }, [id]);

    const submitObservation = async () => {
        if (!obsTitle.trim()) return toast.error('Observation title is required');
        if (!obsPersonId) return toast.error('Person responsible is required');
        if (obsBeforePhotos.length === 0) return toast.error('Before photo is required');
        setSavingObs(true);
        try {
            const person = users.find((u) => String(u.id) === String(obsPersonId));
            const res = await addObservationToWalk(id, {
                title: obsTitle.trim(),
                category: obsCategory || undefined,
                description: obsDescription.trim() || undefined,
                riskRating: obsRisk,
                personResponsibleName: person ? person.name : obsPersonId,
                personResponsibleId: person ? person.id : null,
                company: obsCompany.trim() || undefined,
                dueDate: obsDue || undefined,
            });
            // Upload each staged before-photo now that the observation exists
            if (res.observation) {
                for (const file of obsBeforePhotos) {
                    try {
                        await uploadPhoto(res.observation.id, 'beforePhoto', file);
                    } catch (photoError: any) {
                        toast.error(photoError.message || `Observation saved but "${file.name}" upload failed`);
                    }
                }
            }
            toast.success('Observation recorded');
            setShowNewObs(false);
            setObsTitle(''); setObsCategory(''); setObsDescription(''); setObsPersonId(''); setObsCompany(''); setObsDue(''); setObsBeforePhotos([]);
            await refreshObs();
        } catch (error: any) {
            toast.error(error.message || 'Failed to add observation');
        } finally {
            setSavingObs(false);
        }
    };

    const confirmComplete = async () => {
        setCompleting(true);
        try {
            const res = await completeWalk(id);
            setWalk(res.walk || walk);
            toast.success('Safety walk completed');
            setCompleteOpen(false);
            await refreshObs();
        } catch (error: any) {
            toast.error(error.message || 'Failed to complete walk');
        } finally {
            setCompleting(false);
        }
    };

    const confirmDelete = async () => {
        setDeleting(true);
        try {
            await deleteWalk(id);
            toast.success('Safety walk deleted');
            setDeleteOpen(false);
            navigate('/safety/walks/register');
        } catch (error) {
            toast.error(error instanceof Error ? error.message : 'Failed to delete safety walk');
        } finally {
            setDeleting(false);
        }
    };

    const downloadPdf = async () => {
        setPdfLoading(true);
        try {
            await downloadWalkPdf(id);
        } catch (error) {
            toast.error(error instanceof Error ? error.message : 'Failed to download PDF');
        } finally {
            setPdfLoading(false);
        }
    };

    const saveEdit = async (values: WalkFormValue) => {
        try {
            const selectedProject = projects.find((p) => p.projectName === values.project);
            const conductor = users.find((u) => String(u.id) === values.conductedBy);
            const res = await updateWalk(id, {
                project: values.project,
                location: values.location || (selectedProject ? selectedProject.siteAddress : undefined),
                walkDate: values.walkDate,
                conductedBy: conductor ? conductor.name : (values.conductedBy.startsWith('name:') ? values.conductedBy.slice(5) : values.conductedBy),
                conductedById: conductor ? conductor.id : null,
                participants: values.participants,
                generalComments: values.generalComments.trim() || undefined,
            });
            setWalk(res.walk || walk);
            setEditOpen(false);
            toast.success('Safety walk updated');
        } catch (error) {
            toast.error(error instanceof Error ? error.message : 'Failed to update safety walk');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-24">
                <Loader2 size={24} className="animate-spin text-muted-foreground" />
            </div>
        );
    }

    if (!walk) {
        return (
            <div className="max-w-[1200px] mx-auto px-4 py-16 text-center text-muted-foreground">
                Safety walk not found.
            </div>
        );
    }

    const summary = observations.reduce(
        (acc, o) => {
            acc.total += 1;
            if (o.status === 'Closed') acc.closed += 1;
            else acc.open += 1;
            return acc;
        },
        { total: 0, open: 0, closed: 0 }
    );

    const overdue = observations.filter((o) => o.isOverdue);

    const inputCls = 'w-full h-10 px-3 rounded-lg border border-border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary/30';

    return (
        <div className="pb-10 px-0 sm:px-4 lg:px-0 max-w-[1400px] mx-auto w-full">
            {/* Header */}
            <div className="flex items-start justify-between mb-5 gap-3 flex-wrap">
                <div>
                    <button onClick={() => navigate('/safety/walks/register')} className="text-sm text-primary hover:underline mb-1 inline-flex items-center gap-1">
                        <ChevronLeft size={14} /> Safety Walk Register
                    </button>
                    <div className="flex items-center gap-3 flex-wrap">
                        <h1 className="text-2xl font-medium">{walk.walkNumber}</h1>
                        <StatusPill status={walk.status} />
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                        {walk.project || '—'} • {fmtDate(walk.walkDate)} • {walk.conductedBy || '—'}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    {canUpdate && walk.status !== 'Completed' && (
                        <button
                            onClick={() => setEditOpen(true)}
                            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-border bg-card text-sm font-medium hover:bg-accent"
                        >
                            <Pencil size={15} /> Edit
                        </button>
                    )}
                    <button
                        onClick={downloadPdf}
                        disabled={pdfLoading}
                        className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-border bg-card text-sm font-medium hover:bg-accent disabled:opacity-50"
                    >
                        {pdfLoading ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
                        Download PDF
                    </button>
                    {walk.status !== 'Completed' && (
                        <button
                            onClick={() => setCompleteOpen(true)}
                            disabled={completing}
                            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
                        >
                            {completing && <Loader2 size={15} className="animate-spin" />}
                            Mark Complete
                        </button>
                    )}
                    {canDelete && walk.status === 'Draft' && (
                        <button
                            onClick={() => setDeleteOpen(true)}
                            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700"
                        >
                            <Trash2 size={15} /> Delete
                        </button>
                    )}
                </div>
            </div>

            {/* Tabs (scrollable pills on mobile) */}
            <div className="flex gap-1 border-b border-border mb-6 overflow-x-auto">
                {TABS.map((t) => (
                    <button
                        key={t.key}
                        onClick={() => setTab(t.key)}
                        className={`h-11 px-4 text-sm font-medium whitespace-nowrap border-b-2 -mb-px transition-colors ${
                            tab === t.key ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
                        }`}
                    >
                        {t.label}
                        {t.key === 'observations' && ` (${summary.total})`}
                    </button>
                ))}
            </div>

            {/* ---------------- Overview ---------------- */}
            {tab === 'overview' && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div>
                        <div className="rounded-xl border border-border bg-card p-5 mb-6">
                            <h3 className="text-sm font-semibold mb-4">Walk Information</h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <InfoCell label="Walk Number">{walk.walkNumber}</InfoCell>
                                <InfoCell label="Project">{walk.project}</InfoCell>
                                <InfoCell label="Site Location">{walk.location}</InfoCell>
                                <InfoCell label="Walk Date">{fmtDate(walk.walkDate)}</InfoCell>
                                <InfoCell label="Conducted By">{walk.conductedBy}</InfoCell>
                                <InfoCell label="Participants">
                                    {(walk.participants || []).map((p) => p.name).join(', ') || '—'}
                                </InfoCell>
                            </div>
                            {walk.generalComments && (
                                <div className="mt-4 rounded-lg bg-muted/40 p-4">
                                    <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">General Comments</p>
                                    <p className="text-sm whitespace-pre-wrap">{walk.generalComments}</p>
                                </div>
                            )}
                        </div>

                        {/* Summary cards */}
                        <div className="grid grid-cols-3 gap-4 mb-6">
                            <div className="rounded-xl border border-border bg-card p-4 text-center">
                                <div className="text-2xl font-semibold">{summary.total}</div>
                                <div className="text-xs text-muted-foreground mt-1">Total Observations</div>
                            </div>
                            <div className="rounded-xl border border-border bg-card p-4 text-center">
                                <div className="text-2xl font-semibold text-amber-600">{summary.open}</div>
                                <div className="text-xs text-muted-foreground mt-1">Open Observations</div>
                            </div>
                            <div className="rounded-xl border border-border bg-card p-4 text-center">
                                <div className="text-2xl font-semibold text-emerald-600">{summary.closed}</div>
                                <div className="text-xs text-muted-foreground mt-1">Closed Observations</div>
                            </div>
                        </div>
                    </div>

                    <div>
                        <div className="rounded-xl border border-border bg-card p-5">
                            <h3 className="text-sm font-semibold mb-4">Overdue Management</h3>
                            {overdue.length === 0 ? (
                                <p className="text-sm text-muted-foreground">No overdue observations. All good!</p>
                            ) : (
                                <div className="space-y-3">
                                    {overdue.map((o) => (
                                        <button
                                            key={o.id}
                                            onClick={() => navigate(`/safety/walks/${id}/observations/${o.id}`)}
                                            className="w-full text-left rounded-lg border border-red-200 bg-red-50/50 p-3 hover:bg-red-50"
                                        >
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm font-medium">{o.obsNumber} — {o.title}</span>
                                                <DueIndicator isOverdue dueDate={o.dueDate} />
                                            </div>
                                            <p className="text-xs text-muted-foreground mt-1">Due: {fmtDate(o.dueDate)}</p>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* ---------------- Observations ---------------- */}
            {tab === 'observations' && (
                <div>
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-semibold">Observations ({summary.total})</h3>
                        {walk.status !== 'Completed' && (
                            <button
                                onClick={() => setShowNewObs((v) => !v)}
                                className="inline-flex items-center gap-2 h-9 px-3.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90"
                            >
                                <Plus size={16} /> Add Observation
                            </button>
                        )}
                    </div>

                    {showNewObs && (
                        <div className="rounded-xl border border-border bg-card p-5 mb-6">
                            <h4 className="text-sm font-semibold mb-4">New Observation</h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {/* Line 1: title + category */}
                                <div>
                                    <label className="text-xs font-medium text-muted-foreground mb-1.5 block">OBSERVATION TITLE *</label>
                                    <input value={obsTitle} onChange={(e) => setObsTitle(e.target.value)} placeholder="e.g. Unsecured materials at edge" className={inputCls} />
                                </div>
                                <div>
                                    <label className="text-xs font-medium text-muted-foreground mb-1.5 block">CATEGORY</label>
                                    <Select value={obsCategory || undefined} onValueChange={setObsCategory}>
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select category..." />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {OBSERVATION_CATEGORIES.map((c) => (
                                                <SelectItem key={c} value={c}>{c}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                {/* Line 2: description */}
                                <div className="sm:col-span-2">
                                    <label className="text-xs font-medium text-muted-foreground mb-1.5 block">DESCRIPTION</label>
                                    <textarea value={obsDescription} onChange={(e) => setObsDescription(e.target.value)} placeholder="Describe the observation in detail..." rows={2} className="w-full px-3 py-2 rounded-lg border border-border bg-card text-sm resize-none" />
                                </div>

                                {/* Line 3: risk rating + person responsible */}
                                <div>
                                    <label className="text-xs font-medium text-muted-foreground mb-1.5 block">RISK RATING *</label>
                                    <Select value={obsRisk} onValueChange={(v) => setObsRisk(v as RiskRating)}>
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select risk rating..." />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {RISK_RATINGS.map((r) => (
                                                <SelectItem key={r} value={r}>{r}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <label className="text-xs font-medium text-muted-foreground mb-1.5 block">PERSON RESPONSIBLE *</label>
                                    <Combobox
                                        options={users.map((u) => ({ label: u.name, value: String(u.id) }))}
                                        value={obsPersonId || undefined}
                                        onChange={(value) => setObsPersonId(value || '')}
                                        placeholder="Select person..."
                                        searchPlaceholder="Search users..."
                                        emptyMessage="No users found."
                                    />
                                </div>

                                {/* Line 4: company + due date */}
                                <div>
                                    <label className="text-xs font-medium text-muted-foreground mb-1.5 block">COMPANY / SUBCONTRACTOR</label>
                                    <input value={obsCompany} onChange={(e) => setObsCompany(e.target.value)} placeholder="e.g. BuildRight Pty Ltd" className={inputCls} />
                                </div>
                                <div>
                                    <label className="text-xs font-medium text-muted-foreground mb-1.5 block">DUE DATE *</label>
                                    <input type="date" value={obsDue} onChange={(e) => setObsDue(e.target.value)} className={inputCls} />
                                </div>

                                {/* Line 5: before photo (mandatory, multiple) */}
                                <div className="sm:col-span-2">
                                    <label className="text-xs font-medium text-muted-foreground mb-1.5 block">BEFORE PHOTO <span className="text-red-500">*</span></label>
                                    {obsBeforePhotos.length > 0 && (
                                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
                                            {obsBeforePhotos.map((file, index) => (
                                                <div key={`${file.name}-${index}`} className="rounded-lg border border-border overflow-hidden">
                                                    <img src={URL.createObjectURL(file)} alt={file.name} className="h-28 w-full object-cover bg-muted/30" />
                                                    <div className="flex items-center justify-between px-2 py-1.5 bg-muted/40 text-xs">
                                                        <span className="text-muted-foreground truncate">{file.name}</span>
                                                        <button onClick={() => removeBeforePhoto(index)} className="inline-flex items-center gap-1 text-red-600 hover:underline shrink-0">
                                                            <X size={12} /> Remove
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    <div
                                        {...getRootProps()}
                                        className={`w-full rounded-lg border-2 border-dashed border-border py-8 flex flex-col items-center justify-center text-muted-foreground cursor-pointer hover:bg-accent/40 ${
                                            isDragActive ? 'border-primary bg-primary/5' : ''
                                        }`}
                                    >
                                        <input {...getInputProps()} />
                                        <Upload size={24} className="mb-2" />
                                        <span className="text-sm">{obsBeforePhotos.length > 0 ? 'Add More Photos' : 'Upload Before Photo'}</span>
                                        <span className="text-xs mt-1">PNG, JPG up to 10MB — drag &amp; drop or click to browse (multiple allowed)</span>
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center justify-end gap-3 mt-5">
                                <button onClick={() => setShowNewObs(false)} className="h-9 px-4 rounded-lg border border-border bg-card text-sm font-medium hover:bg-accent">Cancel</button>
                                <button onClick={submitObservation} disabled={savingObs} className="inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50">
                                    {savingObs && <Loader2 size={15} className="animate-spin" />}
                                    Save Observation
                                </button>
                            </div>
                        </div>
                    )}

                    <div className="space-y-4">
                        {observations.map((o) => {
                            // Pending Verification + verify grant → indigo banner + Review Close-Out
                            // entry point (Figma: VerifyCloseOut flow); the card opens the review screen.
                            const awaitingVerification = o.status === 'Pending Verification' && canVerifyObs;
                            const submission = awaitingVerification ? closeOutSubmission(o) : null;
                            return (
                                <div key={o.id} className={awaitingVerification ? 'rounded-xl border border-indigo-200 overflow-hidden' : ''}>
                                    {awaitingVerification && submission && (
                                        <div className="flex items-center justify-between gap-3 flex-wrap bg-indigo-50 border-b border-indigo-200 px-5 py-3">
                                            <div className="flex items-center gap-2 text-sm font-medium text-indigo-700">
                                                <span className="relative flex h-2 w-2 shrink-0">
                                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75" />
                                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-indigo-500" />
                                                </span>
                                                <span>
                                                    Close-out submitted by {submission.who}
                                                    {submission.when ? ` · ${fmtDateTime(submission.when)}` : ''} — awaiting your verification
                                                </span>
                                            </div>
                                            <button
                                                onClick={() => navigate(`/safety/observations/${o.id}/verify-close-out`)}
                                                className="inline-flex items-center gap-1 h-9 px-4 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700"
                                            >
                                                Review Close-Out <ChevronRight size={15} />
                                            </button>
                                        </div>
                                    )}
                                    <button
                                        onClick={() => navigate(awaitingVerification
                                            ? `/safety/observations/${o.id}/verify-close-out`
                                            : `/safety/walks/${id}/observations/${o.id}`)}
                                        className={`w-full text-left bg-card p-5 hover:bg-accent/40 ${awaitingVerification ? '' : 'rounded-xl border border-border'}`}
                                    >
                                        <div className="flex items-center justify-between gap-3 flex-wrap">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="text-sm font-semibold text-primary">{o.obsNumber}</span>
                                                <RiskBadge risk={o.riskRating} />
                                                {o.category && <span className="text-xs text-muted-foreground">{o.category}</span>}
                                                <DueIndicator isOverdue={o.isOverdue} dueDate={o.dueDate} />
                                            </div>
                                            <StatusPill status={o.status} />
                                        </div>
                                        <p className="text-sm font-medium mt-2">{o.title || 'Untitled observation'}</p>
                                        {o.description && <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{o.description}</p>}
                                        <div className="flex items-center gap-3 mt-3 text-xs text-muted-foreground flex-wrap">
                                            <span>Responsible: {o.personResponsibleName || '—'}</span>
                                            {o.dueDate && <span>Due: {fmtDate(o.dueDate)}</span>}
                                            {o.company && <span>{o.company}</span>}
                                        </div>
                                    </button>
                                </div>
                            );
                        })}
                        {observations.length === 0 && (
                            <div className="rounded-xl border border-border bg-card py-12 text-center text-muted-foreground">
                                No observations recorded for this walk
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ---------------- Workflow ---------------- */}
            {tab === 'workflow' && (
                <div className="max-w-3xl">
                    <div className="rounded-xl border border-border bg-card p-6 mb-6">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-sm font-semibold">Safety Walk Progress</h3>
                            <span className="text-2xl font-semibold">{workflow?.percent ?? 0}%</span>
                        </div>
                        <div className="h-2.5 rounded-full bg-muted overflow-hidden">
                            <div className="h-full bg-primary transition-all" style={{ width: `${workflow?.percent ?? 0}%` }} />
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">
                            {workflow?.completed ?? 0}/{workflow?.total ?? 8} steps complete
                        </p>
                    </div>

                    <div className="space-y-1">
                        {(workflow?.steps ?? []).map((step, i) => (
                            <div key={step.key} className="flex gap-4">
                                <div className="flex flex-col items-center">
                                    {step.status === 'Complete' ? (
                                        <CheckCircle2 size={20} className="text-emerald-500" />
                                    ) : step.status === 'In Progress' ? (
                                        <Loader size={20} className="text-amber-500" />
                                    ) : (
                                        <Circle size={20} className="text-muted-foreground/40" />
                                    )}
                                    {i < (workflow?.steps.length ?? 0) - 1 && <div className="w-px flex-1 bg-border my-1" />}
                                </div>
                                <div className={`pb-6 ${step.status === 'Pending' ? 'opacity-50' : ''}`}>
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-medium">{step.label}</span>
                                        <span className={`text-xs px-2 py-0.5 rounded-full border ${
                                            step.status === 'Complete' ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                                : step.status === 'In Progress' ? 'bg-amber-50 text-amber-700 border-amber-200'
                                                    : 'bg-slate-50 text-slate-500 border-slate-200'
                                        }`}>{step.status}</span>
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-1">{step.detail || step.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* ---------------- Audit History ---------------- */}
            {tab === 'audit' && (
                <div className="max-w-3xl">
                    <div className="space-y-1">
                        {(walk.audit || []).map((entry, i) => (
                            <div key={entry.id} className="flex gap-4">
                                <div className="flex flex-col items-center">
                                    <span className="h-2.5 w-2.5 rounded-full bg-primary mt-1.5" />
                                    {i < (walk.audit?.length ?? 0) - 1 && <div className="w-px flex-1 bg-border my-1" />}
                                </div>
                                <div className="pb-6">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-sm font-medium">{entry.action}</span>
                                        <span className="text-xs text-muted-foreground">
                                            {entry.who} • {new Date(entry.date).toLocaleString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                        </span>
                                    </div>
                                    {entry.details && (
                                        <pre className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap font-sans">{JSON.stringify(entry.details)}</pre>
                                    )}
                                </div>
                            </div>
                        ))}
                        {(walk.audit || []).length === 0 && (
                            <div className="rounded-xl border border-border bg-card py-12 text-center text-muted-foreground">No audit history yet</div>
                        )}
                    </div>
                </div>
            )}

            {/* Complete walk confirmation dialog */}
            <AlertDialog open={completeOpen} onOpenChange={setCompleteOpen}>
                <AlertDialogContent className="sm:max-w-[440px] p-0 gap-0 overflow-hidden rounded-2xl border border-gray-200">
                    <AlertDialogHeader className="p-6 pb-4">
                        <div className="flex items-center gap-2">
                            <CheckCircle2 size={20} className="text-emerald-600" />
                            <AlertDialogTitle className="text-lg font-bold text-gray-900">Complete Safety Walk</AlertDialogTitle>
                        </div>
                        <AlertDialogDescription className="text-sm text-gray-500 font-medium pt-1">
                            Are you sure you want to complete <span className="font-bold text-gray-700">{walk.walkNumber}</span>?
                            This moves the walk to <span className="font-bold text-gray-700">Completed</span>. All observations must be closed.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="px-6 pb-6 gap-2">
                        <AlertDialogCancel
                            className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 h-10 rounded-xl text-sm font-bold px-5"
                            disabled={completing}
                        >
                            Cancel
                        </AlertDialogCancel>
                        <button
                            onClick={confirmComplete}
                            disabled={completing}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white h-10 rounded-xl text-sm font-bold px-5 inline-flex items-center gap-2 disabled:opacity-50"
                        >
                            {completing ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />}
                            {completing ? 'Completing…' : 'Complete Walk'}
                        </button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Delete confirmation dialog */}
            <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
                <AlertDialogContent className="sm:max-w-[440px] p-0 gap-0 overflow-hidden rounded-2xl border border-gray-200">
                    <AlertDialogHeader className="p-6 pb-4">
                        <div className="flex items-center gap-2">
                            <Trash2 size={20} className="text-red-500" />
                            <AlertDialogTitle className="text-lg font-bold text-gray-900">Delete Safety Walk</AlertDialogTitle>
                        </div>
                        <AlertDialogDescription className="text-sm text-gray-500 font-medium pt-1">
                            Are you sure you want to delete <span className="font-bold text-gray-700">{walk.walkNumber}</span>?
                            This permanently removes the draft walk and its observations — only delete a walk that was created incorrectly.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="px-6 pb-6 gap-2">
                        <AlertDialogCancel
                            className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 h-10 rounded-xl text-sm font-bold px-5"
                            disabled={deleting}
                        >
                            Cancel
                        </AlertDialogCancel>
                        <button
                            className="bg-red-600 hover:bg-red-700 text-white h-10 rounded-xl text-sm font-bold px-5 inline-flex items-center gap-2 disabled:opacity-50"
                            onClick={confirmDelete}
                            disabled={deleting}
                        >
                            {deleting ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                            Delete Safety Walk
                        </button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {canUpdate && (
                <EditSafetyWalkDrawer
                    open={editOpen}
                    walk={walk}
                    projects={projects}
                    users={users}
                    onClose={() => setEditOpen(false)}
                    onSave={saveEdit}
                />
            )}
        </div>
    );
};
